import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { Button } from '@vio/atlas/Button';
import { useToastController } from '@vio/atlas/context/toastController';
import ContentWrapper from '@vio/atlas/helpers/ContentWrapper';
import { InputTextarea } from '@vio/atlas/InputTextarea';
import { Overlay } from '@vio/atlas/Overlay';
import { Text } from '@vio/atlas/Text';
import { MobileOverlayLayout } from 'components/Layouts/MobileOverlayLayout';
import { getLanguageCode } from 'modules/meta/selectors';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, AnalyticsContext, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { getLocalizedHelpUrl } from '@daedalus/core/src/brand/utils';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { postMessageToWebView, RNWebViewMessageTypes } from '@daedalus/core/src/native';
import { StarRating } from './components/StarRating';
const VALUE_TO_TRIGGER_STORE_REVIEW = 3;
const _exp = /*#__PURE__*/() => ContentWrapper;
const OverlayContent = /*#__PURE__*/styled(_exp())({
  name: "OverlayContent",
  class: "oo2bzjk",
  propsAsIs: true
});
const Link = /*#__PURE__*/styled('a')({
  name: "Link",
  class: "lkd8mn2",
  propsAsIs: false
});
interface ShareYourFeedbackOverlayPropTypes {
  isOpen: boolean;
  onClose: () => void;
}
export const ShareYourFeedbackOverlay = ({
  isOpen,
  onClose
}: ShareYourFeedbackOverlayPropTypes) => {
  const {
    brand
  } = useBrand();
  const [shouldShowSubmit, setShouldShowSubmit] = useState(false);
  const [hasStoreReviewTriggered, setHasStoreReviewTriggered] = useState(false);
  const [value, setValue] = useState<number>(0);
  const [text, setText] = useState<string>('');
  const dispatch = useDispatch();
  const thanksForFeedbackMessage = useToastController();
  const intl = useIntl();
  const languageCode = useSelector(getLanguageCode);
  const onRateChange = value => {
    setValue(value);
    if (value === 0) return setShouldShowSubmit(false);
    setShouldShowSubmit(true);
    if (value >= VALUE_TO_TRIGGER_STORE_REVIEW && !hasStoreReviewTriggered) {
      postMessageToWebView({
        type: RNWebViewMessageTypes.SHOW_IN_APP_REVIEW
      });
      dispatch(trackEvent({
        category: Category.User,
        entity: Entity.AppReview,
        action: Action.Submitted,
        component: 'ShareYourFeedbackOverlayStars',
        analyticsContext: {
          [AnalyticsContext.NpsContext]: {
            npsScore: value
          }
        }
      }));
      setHasStoreReviewTriggered(true);
    }
  };
  const onSubmitFeedback = () => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.AppReview,
      action: Action.Submitted,
      component: 'ShareYourFeedbackOverlayForm',
      analyticsContext: {
        [AnalyticsContext.NpsContext]: {
          npsScore: value,
          openQuestion: text
        }
      }
    }));
    onClose();
    thanksForFeedbackMessage.open(<FormattedMessageWrapper id="shareYourFeedback.thankYouMessage" defaultMessage="Thanks for your feedback!" />);
  };
  return <Overlay isOpen={isOpen}>
      <MobileOverlayLayout headerContent={<FormattedMessageWrapper id="shareYourFeedback" defaultMessage="Share your feedback" />} onBackButtonClick={onClose}>
        <OverlayContent padding="s500" paddingTop="s600">
          <div>
            <Text variant="titleL">
              <FormattedMessageWrapper id="shareYourFeedback.subtitle" defaultMessage="Tell us what we can do better" />
            </Text>

            <ContentWrapper paddingTop="s300">
              <Text variant="bodyS">
                <FormattedMessageWrapper id="shareYourFeedback.text" defaultMessage="We’re not able to respond to queries about specific bookings via this form. For booking assistance or any other questions, go to our <HelpCenter></HelpCenter>" values={{
                HelpCenter: () => <Link href={getLocalizedHelpUrl(brand, languageCode)} target="_blank" rel="noreferrer">
                        <FormattedMessageWrapper id="shareYourFeedback.helpCenter" defaultMessage="help center" />
                      </Link>
              }} />
              </Text>
            </ContentWrapper>

            <ContentWrapper paddingTop="s700" paddingBottom="s400">
              <Text variant="bodyS">
                <FormattedMessageWrapper id="shareYourFeedback.howYouRate" defaultMessage="How would you rate your experience?" />
              </Text>
            </ContentWrapper>

            <StarRating totalStars={5} onRateChange={onRateChange} />

            <ContentWrapper paddingTop="s700" paddingBottom="s200">
              <Text variant="bodyS">
                <FormattedMessageWrapper id="shareYourFeedback.experience" defaultMessage="How could we make your experience better?" />
              </Text>
            </ContentWrapper>
            <InputTextarea id="shareYourFeedback.text" name="shareYourFeedback.text" label={intl.formatMessage({
            id: 'shareYourFeedback.placeholder',
            defaultMessage: 'I wish I could save my details'
          })} onChange={text => setText(text)} inputHeight={124} />
          </div>
          {shouldShowSubmit && <Button fullWidth onClick={onSubmitFeedback}>
              <FormattedMessageWrapper id="shareYourFeedback.cta" defaultMessage="Submit" />
            </Button>}
        </OverlayContent>
      </MobileOverlayLayout>
    </Overlay>;
};

require("./index.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");